import React, { useEffect } from 'react';
import {
  Checkbox,
  Switch,
  FormControlLabel,
  Box,
  Grid,
} from '@material-ui/core';
import { withFormik, FormikProps } from 'formik';
import { IntlShape, injectIntl } from 'react-intl';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { translate } from 'helpers/intl';
import messages from 'containers/SignupPages/IdMemo/messages';
import { ShortBody } from 'components/Typography';
import InputField from 'components/InputField';
import ActionsButtons from 'containers/SignupPages/ActionsButtons';
import {
  StyledBox,
  MemoIdBox,
  StyledCaptions,
  StyledSmallSectionHeader,
  StyledLongBody,
} from 'containers/SignupPages/IdMemo/styles';
import { sanlamBlue } from 'Material-UI/colors';
import { getOrganismeSelector } from 'store/user/selectors';
import { IMAM_POLICIES } from 'constants/globalConstants';

const InnerForm = (
  props: {
    isLoading: boolean;
    onClickPrevious: Function;
    email: string;
  } & FormikProps<FormValues>,
) => {
  const {
    values,
    touched,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    isLoading,
    onClickPrevious,
    setFieldValue,
  } = props;

  const organisme = useSelector(getOrganismeSelector);

  const isImam = IMAM_POLICIES.includes(organisme) && values.email === '';

  useEffect(() => {
    setFieldValue('inMyAddressMail', !isImam);
  }, [isImam]);

  const handleOnClickSaveId = () => {
    !values.isSaveUserId
      ? localStorage.setItem('identifiant', values.identifiant)
      : localStorage.setItem('identifiant', '');
  };

  React.useEffect(() => {
    values.isSaveUserId
      ? localStorage.setItem('identifiant', values.identifiant)
      : localStorage.setItem('identifiant', '');
  }, [values.isSaveUserId]);

  return (
    <React.Fragment>
      <form onSubmit={handleSubmit}>
        <Box mb={3} px={[2, 4, 8]}>
          <StyledSmallSectionHeader color={sanlamBlue} weight="500">
            {values.identifiant}
          </StyledSmallSectionHeader>
          <MemoIdBox marginBottom={2}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  name="isSaveUserId"
                  value={values.isSaveUserId}
                  onChange={handleChange}
                  onClick={handleOnClickSaveId}
                  checked={values.isSaveUserId}
                  data-testid="is-save-user-id"
                />
              }
              label={
                <StyledCaptions>
                  {translate({ ...messages.memoId }, props)}
                </StyledCaptions>
              }
            />
          </MemoIdBox>

          {values.email !== '' && (
            <>
              <Box marginBottom={1.25}>
                <ShortBody>
                  {translate({ ...messages.iWantToSaveMyId }, props)}
                </ShortBody>
              </Box>
              <StyledBox px={3} py={0.75} isRadiusTop>
                <Grid container justify="space-between" alignItems="center">
                  <Grid xs={7} sm={6} item>
                    <StyledLongBody weight="500">
                      {translate({ ...messages.inMyAddressMail }, props)}{' '}
                    </StyledLongBody>
                  </Grid>
                  <Grid xs={3} sm={2} item>
                    <Box marginLeft={4}>
                      <Switch
                        disabled={isImam}
                        checked={values.inMyAddressMail}
                        onChange={handleChange}
                        name="inMyAddressMail"
                        color="primary"
                        data-testid="in-my-address-mail"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </StyledBox>
              <StyledBox px={3} paddingTop={3} paddingBottom={1}>
                <Box marginBottom={3}>
                  <InputField
                    name="email"
                    placeholder={translate({ ...messages.email }, props)}
                    label={translate({ ...messages.email }, props)}
                    value={values.email}
                    error={errors.email && touched.email}
                    helperText={touched.email && errors.email}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                  />
                </Box>
              </StyledBox>
            </>
          )}
        </Box>

        <ActionsButtons
          onClickPrevious={onClickPrevious}
          isLoading={isLoading}
        />
      </form>
    </React.Fragment>
  );
};

export interface FormProps {
  intl: IntlShape;
  onClickPrevious: Function;
  email: string;
  onSubmitStep: Function;
  identifiant: string;
  isLoading: boolean;
}

interface FormValues {
  inMyAddressMail?: boolean;
  isSaveUserId: boolean;
  email: string;
  identifiant: string;
}

const IdMemo = withFormik<FormProps, FormValues>({
  mapPropsToValues: (props) => {
    return {
      isSaveUserId: true,
      inMyAddressMail: true,
      identifiant: props.identifiant,
      email: props.email,
    };
  },

  validationSchema: (props: any) =>
    Yup.object().shape({
      inMyAddressMail: Yup.boolean(),
      email: Yup.string()
        .email(translate({ ...messages.emailInputError }, props))
        .when('inMyAddressMail', {
          is: true,
          then: Yup.string().required(
            translate({ ...messages.requiredField }, props),
          ),
        }),
    }),

  handleSubmit: /* istanbul ignore next */ (values, { props }) => {
    const valuesToSend = {
      login: values.identifiant,
      inMyAddressMail: values.inMyAddressMail,
      email: values.email,
    };
    props.onSubmitStep(valuesToSend);
  },
})(InnerForm);

export default injectIntl(IdMemo);
